import { renderTemplate } from "../templateRenderer";
import { QUOTATION_TEMPLATE, QUOTATION_CSS } from "./loader";

export interface QuotationTemplateContext {
  quotation: {
    id: string;
    quotation_name?: string;
    created_at?: string;
    customer_id?: string | null;
    customer_name?: string | null;
    customer_phone?: string | null;
    customer_email?: string | null;
    customer_address?: string | null;
    temp_customer_name?: string | null;
    temp_customer_phone?: string | null;
    temp_customer_email?: string | null;
    temp_customer_address?: string | null;
    cart_data: QuotationItem[];
    status?: string;
  };
  config: {
    store_name: string;
    address: string;
    phone: string;
    currency?: string;
    receipt_footer?: string;
    [key: string]: any;
  };
  pageSize?: PageSize;
  isForPrint?: boolean;
  validityDays?: number;
}

interface QuotationItem {
  name: string;
  batch_no?: string;
  qty: number;
  price: number;
}

interface ResolvedCustomer {
  isRegistered: boolean;
  name: string;
  phone: string;
  email: string;
  address: string;
}

type PageSize = "A4" | "A3";

const PAGE_SIZES: Record<PageSize, { width: string; font: string }> = {
  A4: { width: "210mm", font: "13.5px" },
  A3: { width: "297mm", font: "15px" },
};

const DEFAULT_STORE_NAME = "POS System";
const DEFAULT_FOOTER = "Thank you for your business!";
const DEFAULT_CUSTOMER_NAME = "Walk-in Customer";
const REF_ID_LENGTH = 8;

const BASE_TEMPLATE = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=device-width,initial-scale=1"/>
  <title>{{ title }}</title>
  <style>{{ styles }}</style>
</head>
<body>
  {{ content | safe }}
</body>
</html>`;

/**
 * Builds a short, human-readable reference number from a quotation id.
 * e.g. "a1b2c3d4-..." -> "Q-A1B2C3D4"
 */
function buildRefNo(quotationId: string): string {
  return `Q-${quotationId.substring(0, REF_ID_LENGTH).toUpperCase()}`;
}

/**
 * Resolves customer details, preferring registered-customer fields and
 * falling back to temp-customer fields when the sale wasn't linked to
 * an account.
 */
function resolveCustomer(quotation: QuotationTemplateContext["quotation"]): ResolvedCustomer {
  const isRegistered = !!quotation.customer_id;

  return {
    isRegistered,
    name: quotation.customer_name || quotation.temp_customer_name || DEFAULT_CUSTOMER_NAME,
    phone: (isRegistered ? quotation.customer_phone : quotation.temp_customer_phone) || "",
    email: (isRegistered ? quotation.customer_email : quotation.temp_customer_email) || "",
    address: (isRegistered ? quotation.customer_address : quotation.temp_customer_address) || "",
  };
}

/** Formats a date as "03 Jul 2026"; defaults to today when none is given. */
function formatQuotationDate(createdAt?: string): string {
  const date = createdAt ? new Date(createdAt) : new Date();
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function calculateSubtotal(items: QuotationItem[]): number {
  return items.reduce((sum, item) => sum + item.price * item.qty, 0);
}

/**
 * Builds the `:root` CSS custom properties that control page size and the
 * on-screen vs. print presentation of the document.
 */
function buildCssVariables(pageSize: PageSize, isForPrint: boolean): string {
  const { width, font } = PAGE_SIZES[pageSize];

  return `
    :root {
      --qt-font: ${font};
      --qt-width: ${width};
      --qt-bg: ${isForPrint ? "#fff" : "#edf2f7"};
      --qt-body-pad: ${isForPrint ? "0" : "28px 20px"};
      --qt-radius: ${isForPrint ? "0" : "8px"};
      --qt-shadow: ${isForPrint ? "none" : "0 4px 24px rgba(67,56,202,0.10)"};
      --qt-card-border: ${isForPrint ? "1px solid #e2e8f0" : "none"};
      --qt-page-size: ${pageSize};
    }
  `;
}

export function renderQuotation(ctx: QuotationTemplateContext): string {
  const { quotation, config, pageSize = "A4", isForPrint = false, validityDays = 30 } = ctx;

  const currency = config.currency || "LKR";
  const refNo = buildRefNo(quotation.id);
  const quotationName = quotation.quotation_name || `Quotation #${quotation.id.substring(0, REF_ID_LENGTH).toUpperCase()}`;
  const customer = resolveCustomer(quotation);
  const items = quotation.cart_data || [];

  const contentContext = {
    storeName: config.store_name || DEFAULT_STORE_NAME,
    address: config.address || "",
    phone: config.phone || "",
    footer: config.receipt_footer || DEFAULT_FOOTER,
    quotationName,
    refNo,
    dateStr: formatQuotationDate(quotation.created_at),
    customerName: customer.name,
    customerPhone: customer.phone,
    customerEmail: customer.email,
    customerAddress: customer.address,
    isRegistered: customer.isRegistered,
    currency,
    items,
    subtotal: calculateSubtotal(items),
    logoUrl: config.store_logo_url || config.logo_url || "",
    validityDays,
  };

  const content = renderTemplate(QUOTATION_TEMPLATE, contentContext);
  const styles = buildCssVariables(pageSize, isForPrint) + "\n" + QUOTATION_CSS;

  return renderTemplate(BASE_TEMPLATE, {
    title: `Quotation - ${refNo}`,
    styles,
    content,
  });
}